import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { applyThemePalettes } from '@/lib/siteTheme';
import { initializeMetaPixel, trackMetaPageView } from '@/lib/metaPixel';

const API = process.env.REACT_APP_BACKEND_URL;
const GA_SCRIPT_ID = 'google-analytics-gtag';
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID;

let activeMeasurementId = null;

function disableGoogleAnalytics() {
  if (typeof window === 'undefined') return;
  const existingScript = document.getElementById(GA_SCRIPT_ID);
  if (existingScript) existingScript.remove();
  activeMeasurementId = null;
}

function ensureGoogleAnalytics(measurementId) {
  if (typeof window === 'undefined') return;
  const normalizedId = String(measurementId || '').trim();
  if (!normalizedId) {
    disableGoogleAnalytics();
    return;
  }

  const existingScript = document.getElementById(GA_SCRIPT_ID);
  if (!existingScript || existingScript.dataset.measurementId !== normalizedId) {
    if (existingScript) existingScript.remove();
    const script = document.createElement('script');
    script.id = GA_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(normalizedId)}`;
    script.dataset.measurementId = normalizedId;
    script.onerror = () => {
      console.error('Error loading Google Analytics script');
    };
    document.head.appendChild(script);
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };

  if (activeMeasurementId !== normalizedId) {
    window.gtag('js', new Date());
    window.gtag('config', normalizedId, { send_page_view: false });
    activeMeasurementId = normalizedId;
  }
}

function trackGoogleAnalyticsPageView(measurementId, pagePath) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  const normalizedId = String(measurementId || '').trim();
  if (!normalizedId) return;

  window.gtag('config', normalizedId, {
    page_path: pagePath,
    send_page_view: true,
  });
}

/**
 * Hook to load site settings from CMS and update document title/meta
 */
export function useSiteSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API}/api/cms/settings`);
        if (response.ok) {
          const data = await response.json();
          setSettings(data);
          
          // Update document title with site name and tagline (only if no page-specific SEO)
          if (data.siteName && !window.__seoPageActive) {
            const title = data.siteTagline 
              ? `${data.siteName} | ${data.siteTagline}`
              : data.siteName;
            document.title = title;
          }
          
          // Update meta description if tagline exists (only if no page-specific SEO)
          if (data.siteTagline && !window.__seoPageActive) {
            const metaDescription = document.querySelector('meta[name="description"]');
            if (metaDescription) {
              metaDescription.setAttribute('content', data.siteTagline);
            }
          }
          
          // Update favicon if configured
          if (data.favicon) {
            let link = document.querySelector("link[rel~='icon']");
            if (!link) {
              link = document.createElement('link');
              link.rel = 'icon';
              document.head.appendChild(link);
            }
            link.href = data.favicon;
          }
          
          // Update OG Image if configured
          if (data.ogImage) {
            let ogImageMeta = document.querySelector('meta[property="og:image"]');
            if (!ogImageMeta) {
              ogImageMeta = document.createElement('meta');
              ogImageMeta.setAttribute('property', 'og:image');
              document.head.appendChild(ogImageMeta);
            }
            ogImageMeta.setAttribute('content', data.ogImage);
            
            // Also update Twitter image
            let twitterImageMeta = document.querySelector('meta[name="twitter:image"]');
            if (!twitterImageMeta) {
              twitterImageMeta = document.createElement('meta');
              twitterImageMeta.setAttribute('name', 'twitter:image');
              document.head.appendChild(twitterImageMeta);
            }
            twitterImageMeta.setAttribute('content', data.ogImage);
          }
          
          // Update OG title (only if no page-specific SEO)
          if (data.siteName && !window.__seoPageActive) {
            let ogTitle = document.querySelector('meta[property="og:title"]');
            if (!ogTitle) {
              ogTitle = document.createElement('meta');
              ogTitle.setAttribute('property', 'og:title');
              document.head.appendChild(ogTitle);
            }
            ogTitle.setAttribute('content', data.siteName);
          }
          
          // Update OG description (only if no page-specific SEO)
          if (data.siteTagline && !window.__seoPageActive) {
            let ogDesc = document.querySelector('meta[property="og:description"]');
            if (!ogDesc) {
              ogDesc = document.createElement('meta');
              ogDesc.setAttribute('property', 'og:description');
              document.head.appendChild(ogDesc);
            }
            ogDesc.setAttribute('content', data.siteTagline);
          }

          if (data.colors) {
            applyThemePalettes(data.colors);
          }

          ensureGoogleAnalytics(data.googleAnalyticsId);
          initializeMetaPixel(META_PIXEL_ID);
        }
      } catch (error) {
        console.error('Error fetching site settings:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    if (!settings?.googleAnalyticsId) {
      disableGoogleAnalytics();
      return;
    }

    ensureGoogleAnalytics(settings.googleAnalyticsId);
    trackGoogleAnalyticsPageView(
      settings.googleAnalyticsId,
      `${location.pathname}${location.search}${location.hash}`
    );
  }, [settings?.googleAnalyticsId, location.pathname, location.search, location.hash]);

  useEffect(() => {
    trackMetaPageView(META_PIXEL_ID);
  }, [location.pathname, location.search, location.hash]);

  return { settings, loading };
}

export default useSiteSettings;
